import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { pdrsAPI, customersAPI, letterheadsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { PDRAttachmentsField, PDRAttachmentList } from "../components/pdr/PDRAttachments";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  FileText,
  Download,
  Upload,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  Building2,
  MapPin,
  Phone,
  Calendar,
  Truck,
  FileSpreadsheet,
  ClipboardList,
  ArrowUpDown,
  Printer,
} from "lucide-react";

const PDR_TYPES = [
  "Sales",
  "Hire",
  "Demonstration",
  "Standby Unit",
  "Loan",
  "Existing RE",
  "Exchange",
];

const PDR_STATUSES = [
  { value: "draft", label: "Draft", color: "bg-slate-100 text-slate-800 border-slate-300" },
  { value: "submitted", label: "Submitted", color: "bg-blue-100 text-blue-800 border-blue-300" },
  { value: "approved", label: "Approved", color: "bg-green-100 text-green-800 border-green-300" },
  { value: "rejected", label: "Rejected", color: "bg-red-100 text-red-800 border-red-300" },
];

const initialFormData = {
  form_date: new Date().toISOString().split("T")[0],
  pdr_type: "Hire",
  letterhead: "none",
  sales_agreement_no: "",
  sales_agreement_date: "",
  rental_agreement_no: "",
  rental_agreement_date: "",
  customer_id: "",
  company_name: "",
  address: "",
  postal_code: "",
  tel_hp_no: "",
  fax_no: "",
  contact_person: "",
  finance_company: "",
  make_model: "",
  serial_no: "",
  delivery_date: "",
  delivery_contact_person: "",
  delivery_tel_no: "",
  place_of_delivery: "",
  transport: "",
  rental_rate: "",
  operator_to: "",
  effective_date: "",
  service_to_inform: false,
  serial_no_to_check: false,
  insurance_coverage: "",
  specification: "",
  special_modification: "",
  notes: "",
  submitted_by: "",
  approval_date: "",
  status: "draft",
};

const PDRsPage = () => {
  const { user } = useAuth();
  const [pdrs, setPdrs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [letterheads, setLetterheads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingPDR, setEditingPDR] = useState(null);
  const [viewingPDR, setViewingPDR] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [editAttachments, setEditAttachments] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [sortBy, setSortBy] = useState("form_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const fileInputRef = useRef(null);

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const canManage = isAdmin || isManager;

  useEffect(() => {
    fetchPDRs();
    fetchCustomers();
    fetchLetterheads();
  }, [page, filterStatus, filterType, dateFrom, dateTo]);

  const fetchPDRs = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 20,
        search: searchTerm || undefined,
        status: filterStatus || undefined,
        pdr_type: filterType || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      };
      const response = await pdrsAPI.getAll(params);
      setPdrs(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Failed to fetch PDRs");
    } finally {
      setLoading(false);
    }
  };

  const fetchLetterheads = async () => {
    try {
      const response = await letterheadsAPI.getAll();
      setLetterheads(response.data);
    } catch (error) {
      console.error("Failed to fetch letterheads:", error);
    }
  };

  const getLetterheadImage = (letterheadId) => {
    if (!letterheadId || letterheadId === "none") return null;
    const lh = letterheads.find((l) => l.id === letterheadId);
    return lh?.image_data || null;
  };

  const fetchCustomers = async () => {
    try {
      const response = await customersAPI.getAll();
      // Sort customers alphabetically by company name
      const sortedCustomers = response.data.sort((a, b) => {
        const nameA = (a.company || a.name || "").toLowerCase();
        const nameB = (b.company || b.name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setCustomers(sortedCustomers);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPDRs();
  };

  const handleOpenDialog = (pdr = null) => {
    if (pdr) {
      setEditingPDR(pdr);
      setFormData({
        form_date: pdr.form_date || "",
        pdr_type: pdr.pdr_type || "Hire",
        letterhead: pdr.letterhead || "poh_li_heng",
        sales_agreement_no: pdr.sales_agreement_no || "",
        sales_agreement_date: pdr.sales_agreement_date || "",
        rental_agreement_no: pdr.rental_agreement_no || "",
        rental_agreement_date: pdr.rental_agreement_date || "",
        customer_id: pdr.customer_id || "",
        company_name: pdr.company_name || "",
        address: pdr.address || "",
        postal_code: pdr.postal_code || "",
        tel_hp_no: pdr.tel_hp_no || "",
        fax_no: pdr.fax_no || "",
        contact_person: pdr.contact_person || "",
        finance_company: pdr.finance_company || "",
        make_model: pdr.make_model || "",
        serial_no: pdr.serial_no || "",
        delivery_date: pdr.delivery_date || "",
        delivery_contact_person: pdr.delivery_contact_person || "",
        delivery_tel_no: pdr.delivery_tel_no || "",
        place_of_delivery: pdr.place_of_delivery || "",
        transport: pdr.transport || "",
        rental_rate: pdr.rental_rate || "",
        operator_to: pdr.operator_to || "",
        effective_date: pdr.effective_date || "",
        service_to_inform: pdr.service_to_inform || false,
        serial_no_to_check: pdr.serial_no_to_check || false,
        insurance_coverage: pdr.insurance_coverage || "",
        specification: pdr.specification || "",
        special_modification: pdr.special_modification || "",
        notes: pdr.notes || "",
        submitted_by: pdr.submitted_by || "",
        approval_date: pdr.approval_date || "",
        status: pdr.status || "draft",
      });
    } else {
      setEditingPDR(null);
      setFormData({
        ...initialFormData,
        form_date: new Date().toISOString().split("T")[0],
        submitted_by: user?.name || "",
      });
    }
    setPendingFiles([]);
    setRemovedAttachmentIds([]);
    setEditAttachments(pdr?.attachments || []);
    setDialogOpen(true);
  };

  const handleRemoveAttachment = (att) => {
    setEditAttachments((prev) => prev.filter((a) => a.id !== att.id));
    setRemovedAttachmentIds((prev) => [...prev, att.id]);
  };

  const handleViewPDR = (pdr) => {
    setViewingPDR(pdr);
    setViewDialogOpen(true);
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find((c) => c.id === customerId);
    if (customer) {
      setFormData({
        ...formData,
        customer_id: customerId,
        company_name: customer.company || customer.name || "",
        address: customer.address || "",
        tel_hp_no: customer.phone || "",
        contact_person: customer.name || "",
      });
    } else {
      setFormData({ ...formData, customer_id: customerId });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const res = editingPDR
        ? await pdrsAPI.update(editingPDR.id, formData)
        : await pdrsAPI.create(formData);
      if (editingPDR && removedAttachmentIds.length > 0) {
        await Promise.all(removedAttachmentIds.map((aid) => pdrsAPI.deleteAttachment(editingPDR.id, aid)));
      }
      if (pendingFiles.length > 0) {
        try {
          await pdrsAPI.uploadAttachments(res.data.id, pendingFiles);
        } catch (err) {
          toast.error(err.response?.data?.detail || "PDR saved, but file upload failed");
        }
      }
      toast.success(editingPDR ? "PDR updated successfully" : "PDR created successfully");
      setDialogOpen(false);
      setEditingPDR(null);
      fetchPDRs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pdrId) => {
    try {
      await pdrsAPI.delete(pdrId);
      toast.success("PDR deleted successfully");
      fetchPDRs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete PDR");
    }
  };

  const handleDownloadPdf = async (pdr) => {
    try {
      toast.info("Generating PDF...");
      await pdrsAPI.downloadPdf(pdr.id, pdr.form_no);
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to generate PDF");
    }
  };

  const handleExport = async () => {
    try {
      toast.info("Exporting PDRs...");
      await pdrsAPI.exportXlsx({
        status: filterStatus || undefined,
        pdr_type: filterType || undefined,
      });
      toast.success("Export completed");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await pdrsAPI.downloadTemplate();
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      toast.info("Importing PDRs...");
      const response = await pdrsAPI.import(file);
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach((err) => toast.warning(err));
      }
      fetchPDRs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = PDR_STATUSES.find((s) => s.value === status) || PDR_STATUSES[0];
    return (
      <Badge className={`${statusConfig.color} rounded-none uppercase text-xs font-bold tracking-wide border`}>
        {statusConfig.label}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const colors = {
      Sales: "bg-green-100 text-green-800 border-green-300",
      Hire: "bg-blue-100 text-blue-800 border-blue-300",
      Demonstration: "bg-purple-100 text-purple-800 border-purple-300",
      "Standby Unit": "bg-amber-100 text-amber-800 border-amber-300",
      Loan: "bg-cyan-100 text-cyan-800 border-cyan-300",
      "Existing RE": "bg-slate-100 text-slate-800 border-slate-300",
      Exchange: "bg-rose-100 text-rose-800 border-rose-300",
    };
    return (
      <Badge className={`${colors[type] || colors.Hire} rounded-none uppercase text-xs font-bold tracking-wide border`}>
        {type}
      </Badge>
    );
  };

  // Sort PDRs client-side
  const sortedPDRs = [...pdrs].sort((a, b) => {
    let aVal = a[sortBy] || "";
    let bVal = b[sortBy] || "";
    if (sortOrder === "asc") {
      return aVal.localeCompare(bVal);
    }
    return bVal.localeCompare(aVal);
  });

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  if (loading && pdrs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="pdrs-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            PDR Forms
          </h1>
          <p className="text-slate-500">
            {total} Pre-Delivery Request{total !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManage && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadTemplate}
                className="rounded-sm"
                data-testid="download-template-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Template
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="rounded-sm"
                data-testid="import-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImport}
                accept=".xlsx,.xls"
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                className="rounded-sm"
                data-testid="export-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </>
          )}
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-blue-500 text-black font-bold uppercase tracking-wider hover:bg-blue-600 rounded-sm border-2 border-transparent hover:border-black"
            data-testid="create-pdr-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New PDR
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search PDRs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" variant="outline" className="rounded-sm">
              Search
            </Button>
          </form>
          <div className="flex gap-2">
            <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-[140px] rounded-sm" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {PDR_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={(v) => { setFilterType(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-[140px] rounded-sm" data-testid="type-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {PDR_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Date Range Filter */}
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span className="text-slate-500">Date Range:</span>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="w-[150px] rounded-sm h-9"
              data-testid="date-from"
            />
            <span className="text-slate-400">to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="w-[150px] rounded-sm h-9"
              data-testid="date-to"
            />
          </div>
          {(dateFrom || dateTo) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}
              className="text-slate-500 hover:text-slate-700 rounded-sm h-9"
            >
              Clear dates
            </Button>
          )}
        </div>
      </div>

      {/* Sort Controls */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <ArrowUpDown className="w-4 h-4" />
        <span>Sort by:</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => toggleSort("form_date")}
          className={`rounded-sm ${sortBy === "form_date" ? "text-blue-600 font-semibold" : ""}`}
        >
          Date {sortBy === "form_date" && (sortOrder === "asc" ? "↑" : "↓")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => toggleSort("form_no")}
          className={`rounded-sm ${sortBy === "form_no" ? "text-blue-600 font-semibold" : ""}`}
        >
          Form No {sortBy === "form_no" && (sortOrder === "asc" ? "↑" : "↓")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => toggleSort("status")}
          className={`rounded-sm ${sortBy === "status" ? "text-blue-600 font-semibold" : ""}`}
        >
          Status {sortBy === "status" && (sortOrder === "asc" ? "↑" : "↓")}
        </Button>
      </div>

      {/* PDR List */}
      {sortedPDRs.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <ClipboardList className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No PDR forms found</p>
            <Button
              onClick={() => handleOpenDialog()}
              variant="link"
              className="mt-2"
            >
              Create your first PDR
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedPDRs.map((pdr) => (
            <Card
              key={pdr.id}
              className="border-slate-200 hover:border-slate-400 transition-colors cursor-pointer"
              onClick={() => handleViewPDR(pdr)}
              data-testid={`pdr-card-${pdr.id}`}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading font-bold text-lg text-slate-900">
                        {pdr.form_no}
                      </h3>
                      {getStatusBadge(pdr.status)}
                      {getTypeBadge(pdr.pdr_type)}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <span>{pdr.company_name || pdr.customer_name || "N/A"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span>{pdr.form_date || "No date"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-slate-400" />
                        <span>{pdr.make_model || "N/A"}</span>
                      </div>
                      {pdr.place_of_delivery && (
                        <div className="flex items-center gap-2 col-span-full">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span className="truncate">{pdr.place_of_delivery}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewPDR(pdr)}
                      className="rounded-sm"
                      data-testid={`view-pdr-${pdr.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadPdf(pdr)}
                      className="rounded-sm"
                      data-testid={`pdf-pdr-${pdr.id}`}
                      title="Download PDF"
                    >
                      <Printer className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenDialog(pdr)}
                      className="rounded-sm"
                      data-testid={`edit-pdr-${pdr.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {canManage && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-sm"
                            data-testid={`delete-pdr-${pdr.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete PDR?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete PDR {pdr.form_no}. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(pdr.id)}
                              className="bg-red-500 hover:bg-red-600"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="rounded-sm"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-slate-600">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="rounded-sm"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {editingPDR ? `Edit PDR - ${editingPDR.form_no}` : "New Pre-Delivery Request"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 pt-4">
            {/* Type & Date Section */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="font-semibold uppercase text-xs tracking-wide">Form Date</Label>
                <Input
                  type="date"
                  value={formData.form_date}
                  onChange={(e) => setFormData({ ...formData, form_date: e.target.value })}
                  className="rounded-sm"
                  data-testid="pdr-form-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold uppercase text-xs tracking-wide">Type *</Label>
                <Select
                  value={formData.pdr_type}
                  onValueChange={(v) => setFormData({ ...formData, pdr_type: v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="pdr-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PDR_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold uppercase text-xs tracking-wide">Letterhead</Label>
                <Select
                  value={formData.letterhead}
                  onValueChange={(v) => setFormData({ ...formData, letterhead: v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="pdr-letterhead">
                    <SelectValue placeholder="Select letterhead..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Letterhead</SelectItem>
                    {letterheads.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold uppercase text-xs tracking-wide">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="pdr-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PDR_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Agreement Section */}
            <div className="border-t border-slate-200 pt-4">
              <h3 className="font-semibold uppercase text-sm tracking-wide mb-3 text-slate-700">Agreement Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Sales Agreement No.</Label>
                  <Input
                    value={formData.sales_agreement_no}
                    onChange={(e) => setFormData({ ...formData, sales_agreement_no: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Sales Agreement Date</Label>
                  <Input
                    type="date"
                    value={formData.sales_agreement_date}
                    onChange={(e) => setFormData({ ...formData, sales_agreement_date: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Rental Agreement No.</Label>
                  <Input
                    value={formData.rental_agreement_no}
                    onChange={(e) => setFormData({ ...formData, rental_agreement_no: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Rental Agreement Date</Label>
                  <Input
                    type="date"
                    value={formData.rental_agreement_date}
                    onChange={(e) => setFormData({ ...formData, rental_agreement_date: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            {/* Customer Information Section */}
            <div className="border-t border-slate-200 pt-4">
              <h3 className="font-semibold uppercase text-sm tracking-wide mb-3 text-slate-700">Customer Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Link to Customer</Label>
                  <Select
                    value={formData.customer_id || "none"}
                    onValueChange={(v) => handleCustomerSelect(v === "none" ? "" : v)}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="pdr-customer">
                      <SelectValue placeholder="Select customer..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Manual Entry --</SelectItem>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.company || c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Company Name</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="rounded-sm"
                    data-testid="pdr-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Contact Person</Label>
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-xs text-slate-500">Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Postal Code</Label>
                  <Input
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Tel/HP No.</Label>
                  <Input
                    value={formData.tel_hp_no}
                    onChange={(e) => setFormData({ ...formData, tel_hp_no: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Fax No.</Label>
                  <Input
                    value={formData.fax_no}
                    onChange={(e) => setFormData({ ...formData, fax_no: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Finance Company</Label>
                  <Input
                    value={formData.finance_company}
                    onChange={(e) => setFormData({ ...formData, finance_company: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            {/* Delivery Information Section */}
            <div className="border-t border-slate-200 pt-4">
              <h3 className="font-semibold uppercase text-sm tracking-wide mb-3 text-slate-700">Delivery Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Make / Model</Label>
                  <Input
                    value={formData.make_model}
                    onChange={(e) => setFormData({ ...formData, make_model: e.target.value })}
                    className="rounded-sm"
                    data-testid="pdr-make-model"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Serial No.</Label>
                  <Input
                    value={formData.serial_no}
                    onChange={(e) => setFormData({ ...formData, serial_no: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Delivery Date</Label>
                  <Input
                    type="date"
                    value={formData.delivery_date}
                    onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Delivery Contact Person</Label>
                  <Input
                    value={formData.delivery_contact_person}
                    onChange={(e) => setFormData({ ...formData, delivery_contact_person: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Delivery Tel/HP No.</Label>
                  <Input
                    value={formData.delivery_tel_no}
                    onChange={(e) => setFormData({ ...formData, delivery_tel_no: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2 sm:col-span-full">
                  <Label className="text-xs text-slate-500">Place of Delivery</Label>
                  <Input
                    value={formData.place_of_delivery}
                    onChange={(e) => setFormData({ ...formData, place_of_delivery: e.target.value })}
                    className="rounded-sm"
                    data-testid="pdr-place-of-delivery"
                  />
                </div>
              </div>
            </div>

            {/* For Rental Purpose Only Section */}
            <div className="border-t border-slate-200 pt-4">
              <h3 className="font-semibold uppercase text-sm tracking-wide mb-3 text-slate-700">For Rental Purpose Only</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Transport</Label>
                  <Input
                    value={formData.transport}
                    onChange={(e) => setFormData({ ...formData, transport: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Rental Rate</Label>
                  <Input
                    value={formData.rental_rate}
                    onChange={(e) => setFormData({ ...formData, rental_rate: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Operator TO</Label>
                  <Input
                    value={formData.operator_to}
                    onChange={(e) => setFormData({ ...formData, operator_to: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Effective Date</Label>
                  <Input
                    type="date"
                    value={formData.effective_date}
                    onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            {/* Additional Fields Section */}
            <div className="border-t border-slate-200 pt-4">
              <h3 className="font-semibold uppercase text-sm tracking-wide mb-3 text-slate-700">Additional Information</h3>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="service_to_inform"
                      checked={formData.service_to_inform}
                      onCheckedChange={(checked) => setFormData({ ...formData, service_to_inform: checked })}
                    />
                    <label htmlFor="service_to_inform" className="text-sm cursor-pointer">
                      Service to Inform
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="serial_no_to_check"
                      checked={formData.serial_no_to_check}
                      onCheckedChange={(checked) => setFormData({ ...formData, serial_no_to_check: checked })}
                    />
                    <label htmlFor="serial_no_to_check" className="text-sm cursor-pointer">
                      Serial No. to Check
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">Insurance Coverage</Label>
                    <Input
                      value={formData.insurance_coverage}
                      onChange={(e) => setFormData({ ...formData, insurance_coverage: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">Submitted By</Label>
                    <Input
                      value={formData.submitted_by}
                      onChange={(e) => setFormData({ ...formData, submitted_by: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Specification</Label>
                  <Textarea
                    value={formData.specification}
                    onChange={(e) => setFormData({ ...formData, specification: e.target.value })}
                    className="rounded-sm min-h-[80px]"
                    placeholder="e.g., LM/LMI/AI Camera/Behind Railing/FE Stand..."
                    data-testid="pdr-specification"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Special Modification/Attachment</Label>
                  <Textarea
                    value={formData.special_modification}
                    onChange={(e) => setFormData({ ...formData, special_modification: e.target.value })}
                    className="rounded-sm min-h-[60px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="rounded-sm min-h-[60px]"
                  />
                </div>
              </div>
            </div>

            <PDRAttachmentsField
              pdrId={editingPDR?.id}
              attachments={editAttachments}
              onRemoveExisting={handleRemoveAttachment}
              pendingFiles={pendingFiles}
              setPendingFiles={setPendingFiles}
            />

            {/* Form Actions */}
            <div className="flex gap-3 pt-4 border-t border-slate-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-blue-500 text-black font-bold uppercase hover:bg-blue-600 rounded-sm"
                data-testid="pdr-submit"
              >
                {saving ? "Saving..." : editingPDR ? "Update PDR" : "Create PDR"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <FileText className="w-5 h-5" />
              PDR Details - {viewingPDR?.form_no}
            </DialogTitle>
          </DialogHeader>
          {viewingPDR && (
            <div className="space-y-6 pt-4">
              {/* Letterhead */}
              {getLetterheadImage(viewingPDR.letterhead) && (
                <div className="border-b border-slate-200 pb-4">
                  <img
                    src={getLetterheadImage(viewingPDR.letterhead)}
                    alt="Letterhead"
                    className="max-h-24 mx-auto object-contain"
                  />
                </div>
              )}

              {/* Header Info */}
              <div className="flex items-center gap-3 flex-wrap">
                {getStatusBadge(viewingPDR.status)}
                {getTypeBadge(viewingPDR.pdr_type)}
                <span className="text-slate-500 text-sm">Form Date: {viewingPDR.form_date || "N/A"}</span>
              </div>

              {/* Customer Info */}
              <div className="bg-slate-50 p-4 rounded-sm space-y-2">
                <h4 className="font-semibold text-sm uppercase text-slate-700">Customer Information</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-slate-500">Company:</span> {viewingPDR.company_name || viewingPDR.customer_name || "N/A"}</div>
                  <div><span className="text-slate-500">Contact:</span> {viewingPDR.contact_person || "N/A"}</div>
                  <div><span className="text-slate-500">Tel:</span> {viewingPDR.tel_hp_no || "N/A"}</div>
                  <div><span className="text-slate-500">Fax:</span> {viewingPDR.fax_no || "N/A"}</div>
                  <div className="col-span-2"><span className="text-slate-500">Address:</span> {viewingPDR.address || "N/A"} {viewingPDR.postal_code}</div>
                </div>
              </div>

              {/* Delivery Info */}
              <div className="bg-slate-50 p-4 rounded-sm space-y-2">
                <h4 className="font-semibold text-sm uppercase text-slate-700">Delivery Information</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-slate-500">Make/Model:</span> {viewingPDR.make_model || "N/A"}</div>
                  <div><span className="text-slate-500">Serial No:</span> {viewingPDR.serial_no || "N/A"}</div>
                  <div><span className="text-slate-500">Delivery Date:</span> {viewingPDR.delivery_date || "N/A"}</div>
                  <div><span className="text-slate-500">Contact:</span> {viewingPDR.delivery_contact_person || "N/A"}</div>
                  <div className="col-span-2"><span className="text-slate-500">Place of Delivery:</span> {viewingPDR.place_of_delivery || "N/A"}</div>
                </div>
              </div>

              {/* Rental Info */}
              {(viewingPDR.transport || viewingPDR.rental_rate || viewingPDR.operator_to) && (
                <div className="bg-blue-50 p-4 rounded-sm space-y-2">
                  <h4 className="font-semibold text-sm uppercase text-blue-700">Rental Information</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500">Transport:</span> {viewingPDR.transport || "N/A"}</div>
                    <div><span className="text-slate-500">Rental Rate:</span> {viewingPDR.rental_rate || "N/A"}</div>
                    <div><span className="text-slate-500">Operator TO:</span> {viewingPDR.operator_to || "N/A"}</div>
                    <div><span className="text-slate-500">Effective Date:</span> {viewingPDR.effective_date || "N/A"}</div>
                  </div>
                </div>
              )}

              {/* Specification */}
              {viewingPDR.specification && (
                <div className="bg-slate-50 p-4 rounded-sm space-y-2">
                  <h4 className="font-semibold text-sm uppercase text-slate-700">Specification</h4>
                  <p className="text-sm whitespace-pre-wrap">{viewingPDR.specification}</p>
                </div>
              )}

              {/* Notes */}
              {viewingPDR.notes && (
                <div className="bg-slate-50 p-4 rounded-sm space-y-2">
                  <h4 className="font-semibold text-sm uppercase text-slate-700">Notes</h4>
                  <p className="text-sm whitespace-pre-wrap">{viewingPDR.notes}</p>
                </div>
              )}

              <div className="bg-slate-50 p-4 rounded-sm space-y-2" data-testid="pdr-view-attachments">
                <h4 className="font-semibold text-sm uppercase text-slate-700">Supporting Files</h4>
                <PDRAttachmentList pdrId={viewingPDR.id} attachments={viewingPDR.attachments || []} />
              </div>

              {/* Footer */}
              <div className="text-sm text-slate-500 flex justify-between">
                <span>Submitted By: {viewingPDR.submitted_by || "N/A"}</span>
                <span>Created: {new Date(viewingPDR.created_at).toLocaleDateString()}</span>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={() => setViewDialogOpen(false)}
                  className="flex-1 rounded-sm"
                >
                  Close
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleDownloadPdf(viewingPDR)}
                  className="flex-1 rounded-sm"
                  data-testid="view-pdf-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button
                  onClick={() => {
                    setViewDialogOpen(false);
                    handleOpenDialog(viewingPDR);
                  }}
                  className="flex-1 bg-slate-800 text-white hover:bg-slate-900 rounded-sm"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PDRsPage;
